import React, { useState } from 'react';

const HistoryChart = ({ history = [], language, handleReviewSession, page = 0, setPage, itemsPerPage = 10, minScore = 75, isAdminPreview = false, lastReviewedId = null }) => {
    const ITEMS_PER_PAGE = itemsPerPage;
    const totalPages = Math.max(1, Math.ceil(history.length / ITEMS_PER_PAGE));

    const paginatedHistory = history.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
    // Always show ITEMS_PER_PAGE bars - fill with empty slots if needed
    const displayBars = [...paginatedHistory, ...Array(Math.max(0, ITEMS_PER_PAGE - paginatedHistory.length)).fill(null)];

    return (
        <div className="bg-white dark:bg-[#121212] rounded-2xl md:rounded-[2rem] px-8 py-4 md:p-10 border border-gray-200 dark:border-white/5 flex flex-col shadow-sm dark:shadow-none">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-8 gap-3">
                <h3 className="font-black text-xs uppercase tracking-widest text-gray-500 dark:text-gray-500 flex items-center gap-2 flex-shrink-0">
                    <svg className="w-3 md:w-4 h-3 md:h-4 text-gray-400 dark:text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {language === 'fr' ? 'Historique' : 'History'}
                </h3>

                {/* Pagination Controls — axe temporel : récent à GAUCHE, ancien à DROITE.
                    Flèche LEFT = vers les plus récents (page--), flèche RIGHT = remonter le temps (page++). */}
                <div className="flex items-center gap-1 md:gap-2 bg-gray-50 dark:bg-white/5 rounded-lg md:rounded-xl px-1.5 md:px-2 py-1 animate-in fade-in transition-all duration-300 flex-shrink-0">
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="p-0.5 md:p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="w-3 h-3 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <span className="text-[8px] md:text-[10px] font-black text-gray-400 dark:text-white/40 min-w-[35px] md:min-w-[30px] text-center uppercase tracking-tighter">
                        {page + 1}/{totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= totalPages - 1}
                        className="p-0.5 md:p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    >
                        <svg className="w-3 h-3 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="flex items-end justify-between gap-0.5 md:gap-1 h-72 md:h-96 relative pl-10 md:pl-12">
                {/* Grid lines and labels */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-50 left-0">
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map((val) => (
                        <div key={val} className="w-full h-0 flex items-center group">
                            <div className="w-full border-b border-gray-100 dark:border-white/5" />
                            <span className="text-[8px] md:text-[9px] font-black text-gray-400 dark:text-gray-400 absolute -left-10 md:-left-12 w-8 md:w-10 text-right uppercase tracking-tighter whitespace-nowrap">{val}</span>
                        </div>
                    ))}
                </div>

                <div className="absolute inset-0 left-10 md:left-12 right-0 flex flex-col justify-between pointer-events-none opacity-50">
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0].map((val) => (
                        <div key={val} className="w-full h-0">
                            <div className="w-full border-b border-gray-100 dark:border-white/5" />
                        </div>
                    ))}
                </div>

                {/* Minimum score threshold line */}
                <div
                    className="absolute left-0 right-0 pointer-events-none z-30"
                    style={{ bottom: `${minScore}%` }}
                >
                    <div className="w-full h-[2px] bg-gradient-to-r from-gray-400/80 via-gray-300/60 to-gray-400/80 dark:from-white/40 dark:via-white/25 dark:to-white/40 rounded-full" />
                </div>

                {/* Bars : ordre natif (DESC par date) → récent à GAUCHE, ancien à DROITE */}
                {displayBars.map((item, idx) => {
                    // Calculate opacity based on position (every 5 items) - inverted for gradient effect
                    const opacityLevels = [1, 0.9, 0.8, 0.7, 0.6];
                    const opacityIndex = idx % 5;
                    const baseOpacity = opacityLevels[opacityIndex];
                    const isLastReviewed = item && lastReviewedId != null && String(item.id) === String(lastReviewedId);

                    return (
                    <div key={idx} className="relative group flex-1 flex flex-col items-center justify-end h-full z-10 hover:z-40 min-w-0">

                        {/* Bar - only render if item exists */}
                        {item ? (
                        <div
                            onClick={() => handleReviewSession(item.id, item.isVuExamen)}
                            className={`cursor-pointer w-2/3 rounded-t-xl transition-all duration-500 ease-out hover:brightness-125 shadow-[0_4px_12px_rgba(0,0,0,0.15)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)] relative z-20 ${isLastReviewed ? 'ring-2 ring-blue-400 dark:ring-blue-500 ring-offset-1 dark:ring-offset-[#121212]' : ''}`}
                            style={{
                                height: `${item.score}%`,
                                opacity: baseOpacity,
                                backgroundColor: item.score >= minScore ? '#10b981' : (item.score >= minScore - 10 ? '#fb923c' : '#ef4444'),
                                backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0.2), transparent 50%, rgba(0,0,0,0.15))'
                            }}
                        >
                            {/* Tooltip - positioned just above the bar */}
                            <div
                                onClick={() => handleReviewSession(item.id, item.isVuExamen)}
                                className="cursor-pointer absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-[9999] pointer-events-none group-hover:pointer-events-auto scale-75 md:scale-90 group-hover:scale-75 md:group-hover:scale-100 origin-bottom whitespace-nowrap"
                            >
                                <div className="bg-gray-900 dark:bg-gray-800 text-white text-[8px] md:text-[10px] font-black rounded-xl py-2 md:py-2.5 px-3 md:px-4 shadow-2xl uppercase tracking-widest border border-gray-700">
                                    {/* Exam Title */}
                                    {item.title && (
                                        <div className="text-white mb-2 pb-2 border-b border-gray-700 dark:border-gray-600">
                                            {(language === 'en' ? item.title?.en : item.title?.fr) || item.title?.fr || 'Examen'}
                                            {isAdminPreview && <span className="ml-1 text-amber-400 font-mono normal-case">#{item.id}</span>}
                                        </div>
                                    )}
                                    <div className="text-gray-400 text-[7px] md:text-[9px] mb-1">Date</div>
                                    <div className="mb-2">{item.date ? new Date(item.date).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</div>
                                    <div className="text-gray-400 text-[7px] md:text-[9px] mb-1">Score</div>
                                    <div className="mb-2">{item.score}%</div>
                                    <div className="text-gray-400 text-[7px] md:text-[9px] mb-1">Temps</div>
                                    <div>{Math.floor(item.duration / 60)} min {String(item.duration % 60).padStart(2, '0')}</div>
                                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-800 rotate-45 border-b border-r border-gray-700"></div>
                                </div>
                            </div>
                        </div>
                        ) : (
                        <div className="w-2/3 rounded-t-xl" />
                        )}
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default HistoryChart;
